import { Component, OnInit } from '@angular/core';
import { PageTitleService } from '@/layout/service/page.title.service';
import { HangfireJobsApiService } from './hangfire-jobs-api.service';
import { HangfireJobInfoDTO } from './hangfire-jobs.models';

/**
 * Uniformax admin page that publishes the live + curated catalog of Uniformax Hangfire jobs.
 *
 * - Static narrative comes from server-side HangfireJobsRegistry.
 * - Live state (last run, next run, status, error) comes from Hangfire's monitoring API.
 *
 * Read-only. Schedule changes still go through Startup.cs and the Hangfire dashboard.
 *
 * Lives at /administration/manage/hangfire-jobs.
 */
@Component({
  selector: 'app-hangfire-jobs',
  standalone: false,
  templateUrl: './hangfire-jobs.component.html',
  styleUrls: ['./hangfire-jobs.component.scss'],
})
export class HangfireJobsComponent implements OnInit {
  jobs: HangfireJobInfoDTO[] = [];
  isLoading = false;
  errorMessage?: string;
  expanded = new Set<string>();

  constructor(
    private api: HangfireJobsApiService,
    private pageTitleService: PageTitleService,
  ) {}

  async ngOnInit() {
    this.pageTitleService.setPageTitle('Administration: Hangfire Jobs');
    await this.load();
  }

  async load() {
    this.isLoading = true;
    this.errorMessage = undefined;
    try {
      this.jobs = await this.api.getList();
    } catch (err: any) {
      this.jobs = [];
      this.errorMessage = err?.message || String(err);
    } finally {
      this.isLoading = false;
    }
  }

  toggle(name: string) {
    if (this.expanded.has(name)) this.expanded.delete(name);
    else this.expanded.add(name);
  }

  isExpanded(name: string): boolean {
    return this.expanded.has(name);
  }

  statePillClass(state?: string): string {
    if (!state) return 'never';
    const s = state.toLowerCase();
    if (s === 'succeeded') return 'succeeded';
    if (s === 'failed') return 'failed';
    if (s === 'processing') return 'processing';
    if (s === 'scheduled' || s === 'enqueued') return 'scheduled';
    return 'never';
  }

  stateDisplay(job: HangfireJobInfoDTO): string {
    return job.LastState || (job.LastExecutionUtc ? 'unknown' : 'never run');
  }

  trackByName(_: number, j: HangfireJobInfoDTO) {
    return j.Name;
  }
}
